import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { assessmentAPI } from "../utils/api";
import { useToast } from "../utils/ToastContext";
import { useAuth } from "../utils/AuthContext";
import { assessmentData } from "../data/assessmentData";
import api from "../utils/api";
import "./History.css";

const RATING_CLASS = {
  "Level A": "rating-A",
  "Level B": "rating-B",
  "Level B*": "rating-Bstar",
  "Level C": "rating-C",
  "Level D": "rating-D",
};

const RATING_COLORS = {
  "Level A": "#16a34a",
  "Level B": "#2563eb",
  "Level B*": "#dc2626",
  "Level C": "#d97706",
  "Level D": "#dc2626",
};

function formatAuditDate(value) {
  if (!value) return "Date unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function auditName(actor) {
  return actor?.fullName || actor?.username || "Unknown";
}

function buildScoreMap(indicators) {
  if (!Array.isArray(indicators)) return new Map();

  return new Map(
    indicators
      .filter((item) => item && item.indicator)
      .map((item) => [item.indicator, item.score]),
  );
}

function buildEvidenceMap(assessment) {
  if (!Array.isArray(assessment.indicators)) {
    return {};
  }

  return assessment.indicators.reduce((acc, item) => {
    if (item?.indicator && item?.evidence) {
      acc[item.indicator] = item.evidence;
    }

    return acc;
  }, {});
}

function HistoryIndicator({ indicator }) {
  const [showEvidence, setShowEvidence] = useState(false);
  const hasEvidence =
    indicator.evidence &&
    (indicator.evidence.text ||
      (Array.isArray(indicator.evidence.attachments) &&
        indicator.evidence.attachments.length > 0));

  return (
    <div className="history-indicator-detail">
      <div className="history-indicator-main">
        <div className="history-indicator-code">{indicator.code}</div>

        <div className="history-indicator-text">
          <div className="history-indicator-name">{indicator.name}</div>

          <div className="history-indicator-meta">
            {indicator.isCore ? "CORE indicator" : "Standard indicator"}
            {indicator.v4note ? ` · ${indicator.v4note}` : ""}
          </div>

          {hasEvidence && (
            <button
              type="button"
              className={`evidence-toggle ${
                showEvidence ? "evidence-toggle-open" : ""
              }`}
              onClick={() => setShowEvidence((prev) => !prev)}
            >
              <span className="evidence-toggle-icon">
                {showEvidence ? "⌃" : "⌄"}
              </span>

              {showEvidence ? "Hide evidence" : "View evidence"}

              {Array.isArray(indicator.evidence.attachments) &&
                indicator.evidence.attachments.length > 0 && (
                  <span className="evidence-count">
                    {indicator.evidence.attachments.length}
                  </span>
                )}
            </button>
          )}
        </div>

        <div
          className={`history-score-pill history-score-${
            indicator.score ?? "na"
          }`}
        >
          {indicator.score ?? "N/A"}
        </div>
      </div>

      {showEvidence && hasEvidence && (
        <div className="history-evidence-panel">
          <div className="history-evidence-header">
            <div>
              <div className="history-evidence-title">Supporting evidence</div>
              <div className="history-evidence-subtitle">
                Documentation provided for this indicator
              </div>
            </div>
          </div>

          {indicator.evidence.text && (
            <div className="history-evidence-text">
              {indicator.evidence.text}
            </div>
          )}

          {Array.isArray(indicator.evidence.attachments) &&
            indicator.evidence.attachments.length > 0 && (
              <div className="history-evidence-files">
                <div className="history-evidence-files-title">Attachments</div>

                {indicator.evidence.attachments.map((file) => (
                  <a
                    key={file._id || file.publicId}
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="history-evidence-file"
                    title={`Open ${file.originalName}`}
                  >
                    <div className="history-evidence-file-icon">📎</div>

                    <div className="history-evidence-file-info">
                      <div className="history-evidence-file-name">
                        {file.originalName}
                      </div>

                      <div className="history-evidence-file-meta">
                        {file.mimeType || "Attached document"}
                      </div>
                    </div>

                    <div className="history-evidence-file-open">↗</div>
                  </a>
                ))}
              </div>
            )}

          <div className="history-evidence-footer">
            <span>✓</span>
            Evidence documented
          </div>
        </div>
      )}
    </div>
  );
}

//hisory item
function HistoryItem({ assessment, onDelete, isAdmin }) {
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  const computed = assessment.computed || {};
  const scoreMap = buildScoreMap(assessment.indicators);
  const evidenceMap = buildEvidenceMap(assessment);
  const rating = computed.rating || "Level D";
  const ratingColor = RATING_COLORS[rating] || "#64748b";

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await assessmentAPI.delete(assessment._id);
      toast("Assessment deleted", "success");
      onDelete(assessment._id);
    } catch {
      toast("Delete failed", "error");
    } finally {
      setDeleting(false);
      setShowConfirm(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get(`/assessments/${assessment._id}/export`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(response.data);

      const link = document.createElement("a");
      link.href = url;
      link.download = `assessment-${assessment._id}.xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      toast("Excel downloaded", "success");
    } catch (err) {
      console.error(err);
      toast("Excel export failed", "error");
    }
  };

  const pillars = [
    {
      label: "P1 Training",
      val: computed.p1Weighted,
      max: 450,
      raw: computed.p1Raw,
      maxRaw: 104,
      color: "var(--navy)",
    },
    {
      label: "P2 Research",
      val: computed.p2Weighted,
      max: 300,
      raw: computed.p2Raw,
      maxRaw: 64,
      color: "var(--green)",
    },
    {
      label: "P3 Community",
      val: computed.p3Weighted,
      max: 250,
      raw: computed.p3Raw,
      maxRaw: 56,
      color: "var(--amber)",
    },
  ];

  const detailedPillars = assessmentData.pillars.map((pillar) => ({
    ...pillar,
    domains: pillar.domains.map((domain) => ({
      ...domain,
      indicators: domain.indicators.map((indicator) => ({
        ...indicator,
        score: scoreMap.has(indicator.code)
          ? scoreMap.get(indicator.code)
          : null,
        evidence: evidenceMap[indicator.code] || null,
      })),
    })),
  }));

  return (
    <>
      <div className="history-item">
        <div
          className="history-item-header"
          onClick={() => setExpanded((current) => !current)}
        >
          <div className="history-left">
            <div>
              <div className="history-institution">
                {assessment.institutionName}
              </div>
              <div className="history-sub">
                <span className="history-audit-summary">
                  Created by {auditName(assessment.createdBy)} ·{" "}
                  {formatAuditDate(assessment.createdAt)}
                </span>
                <span className="history-audit-summary">
                  Updated by {auditName(assessment.updatedBy)} ·{" "}
                  {formatAuditDate(assessment.updatedAt)}
                </span>
                <span>
                  📅{" "}
                  {new Date(assessment.assessmentDate).toLocaleDateString(
                    "en-GB",
                    { day: "2-digit", month: "short", year: "numeric" },
                  )}
                </span>
                <span>👤 {assessment.leadAssessor}</span>
                <span>🏛 {assessment.generationTier}</span>
                <span
                  style={{
                    padding: "0.05rem 0.4rem",
                    borderRadius: "4px",
                    background:
                      assessment.status === "complete" ? "#dcfce7" : "#fef3c7",
                    color:
                      assessment.status === "complete" ? "#16a34a" : "#d97706",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                  }}
                >
                  {assessment.status === "complete" ? "✓ Complete" : "⋯ Draft"}
                </span>
              </div>
            </div>
          </div>
          <div className="history-right">
            {isAdmin ? (
              <>
                <div style={{ textAlign: "right" }}>
                  <div
                    style={{
                      fontSize: "1.3rem",
                      fontWeight: 800,
                      color: ratingColor,
                      fontFamily: "var(--font-display)",
                      lineHeight: 1,
                    }}
                  >
                    {(computed.grandTotal || 0).toFixed(0)}
                  </div>
                  <div style={{ fontSize: "0.68rem", color: "var(--text-3)" }}>
                    / 1,000 pts
                  </div>
                </div>
                <span
                  className={`rating-badge ${RATING_CLASS[rating] || "rating-D"}`}
                  style={{ fontSize: "0.75rem", padding: "0.25rem 0.7rem" }}
                >
                  {rating}
                </span>
              </>
            ) : (
              <span
                className="rating-badge"
                style={{
                  fontSize: "0.75rem",
                  padding: "0.25rem 0.7rem",
                  color: "red",
                }}
              >
                Results hidden
              </span>
            )}
            <span className={`chevron${expanded ? " open" : ""}`}>▼</span>
          </div>
        </div>

        {expanded && (
          <div className="history-body">
            <div
              className="history-audit-panel"
              aria-label="Assessment activity"
            >
              <div>
                <span className="history-audit-label">Created</span>
                <strong>{auditName(assessment.createdBy)}</strong>
                <time dateTime={assessment.createdAt || undefined}>
                  {formatAuditDate(assessment.createdAt)}
                </time>
              </div>
              <div>
                <span className="history-audit-label">Last updated</span>
                <strong>{auditName(assessment.updatedBy)}</strong>
                <time dateTime={assessment.updatedAt || undefined}>
                  {formatAuditDate(assessment.updatedAt)}
                </time>
              </div>
            </div>
            {isAdmin ? (
              <>
                <div className="history-pillar-bars">
                  {pillars.map((pillar) => (
                    <div key={pillar.label} className="h-pillar-bar">
                      <div className="h-pillar-name">{pillar.label}</div>
                      <div className="h-pillar-score">
                        {(pillar.val || 0).toFixed(0)}{" "}
                        <span
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: 400,
                            color: "var(--text-3)",
                          }}
                        >
                          / {pillar.max}
                        </span>
                      </div>
                      <div className="progress-bar" style={{ height: "5px" }}>
                        <div
                          style={{
                            height: "100%",
                            width: `${Math.min(((pillar.val || 0) / pillar.max) * 100, 100)}%`,
                            background: pillar.color,
                            borderRadius: "3px",
                            transition: "width 0.5s",
                          }}
                        />
                      </div>
                      <div
                        style={{
                          fontSize: "0.7rem",
                          color: "var(--text-3)",
                          marginTop: "0.25rem",
                        }}
                      >
                        Raw: {pillar.raw || 0} / {pillar.maxRaw}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="history-completion-row">
                  <div className="progress-bar" style={{ flex: 1 }}>
                    <div
                      style={{
                        height: "100%",
                        width: `${computed.completionPct || 0}%`,
                        background: "var(--navy)",
                        borderRadius: "4px",
                        transition: "width 0.5s",
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: "0.8rem",
                      color: "var(--text-2)",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {computed.completionPct || 0}% indicators scored
                  </span>
                  <span
                    className={`gatekeeper-status ${computed.gatekeeperPassed ? "gatekeeper-pass" : "gatekeeper-fail"}`}
                    style={{ fontSize: "0.75rem", padding: "0.25rem 0.6rem" }}
                  >
                    {computed.gatekeeperPassed
                      ? "✅ Gatekeeper OK"
                      : "❌ Gatekeeper Blocked"}
                  </span>
                </div>

                <div className="history-score-details">
                  <div className="history-score-details-title">
                    Full Indicator Scores
                  </div>

                  {detailedPillars.map((pillar) => {
                    const weightedScore = computed[`${pillar.id}Weighted`];

                    return (
                      <section
                        key={pillar.id}
                        className="history-pillar-detail"
                      >
                        <div className="history-pillar-detail-header">
                          <div>
                            <div className="history-pillar-detail-title">
                              {pillar.emoji} {pillar.name}
                            </div>
                            <div className="history-pillar-detail-subtitle">
                              {pillar.code} · {pillar.weightLabel} weight · Max{" "}
                              {pillar.maxWeighted} points
                            </div>
                          </div>
                          <div className="history-pillar-detail-score">
                            {typeof weightedScore === "number"
                              ? weightedScore.toFixed(1)
                              : "0.0"}
                          </div>
                        </div>

                        {pillar.domains.map((domain) => (
                          <div
                            key={domain.id}
                            className="history-domain-detail"
                          >
                            <div className="history-domain-detail-header">
                              <div className="history-domain-code">
                                {domain.code}
                              </div>
                              <div className="history-domain-name">
                                {domain.name}
                              </div>
                            </div>

                            <div className="history-indicator-list">
                              {domain.indicators.map((indicator) => (
                                <HistoryIndicator
                                  key={indicator.code}
                                  indicator={indicator}
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                      </section>
                    );
                  })}
                </div>

                <div className="history-actions">
                  <button
                    className="btn  btn-outline btn-sm"
                    onClick={() =>
                      navigate(`/assessment/${assessment._id}/edit`)
                    }
                  >
                    ✏️ Edit
                  </button>
                  <button
                    className="btn btn-gold btn-sm"
                    onClick={handleExport}
                  >
                    📥 Export Excel
                  </button>
                  <button
                    className="btn btn-outline btn-sm"
                    style={{ color: "var(--red)", borderColor: "var(--red)" }}
                    onClick={() => setShowConfirm(true)}
                  >
                    🗑 Delete
                  </button>
                </div>
              </>
            ) : (
              <div
                className="history-actions"
                style={{ justifyContent: "space-between" }}
              >
                <div style={{ color: "var(--text-2)", fontSize: "0.85rem" }}>
                  Assessment details and scoring results are visible to admins
                  only.
                </div>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => navigate(`/assessment/${assessment._id}/edit`)}
                >
                  ✏️ Edit
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {showConfirm && (
        <div className="modal-overlay" onClick={() => setShowConfirm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Delete Assessment?</h2>
            <div className="modal-body">
              This will permanently delete the assessment for{" "}
              <strong>{assessment.institutionName}</strong> dated{" "}
              <strong>
                {new Date(assessment.assessmentDate).toLocaleDateString(
                  "en-GB",
                )}
              </strong>
              . This action cannot be undone.
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-outline"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <span className="spinner" />
                ) : (
                  "🗑 Delete Permanently"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function HistoryPage() {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const toast = useToast();
  const { isAdmin } = useAuth();

  const load = useCallback(() => {
    setLoading(true);
    assessmentAPI
      .getAll()
      .then(({ data }) => setAssessments(data.data))
      .catch((error) =>
        toast(
          error.response?.status === 401
            ? "Your session expired. Please sign in again."
            : "Failed to load history",
          "error",
        ),
      )
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = useCallback((id) => {
    setAssessments((current) => current.filter((item) => item._id !== id));
  }, []);

  const filtered = assessments.filter((assessment) => {
    const matchSearch =
      assessment.institutionName.toLowerCase().includes(search.toLowerCase()) ||
      assessment.leadAssessor.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === "all" || assessment.status === filter;
    return matchSearch && matchFilter;
  });

  const stats = {
    total: assessments.length,
    complete: assessments.filter(
      (assessment) => assessment.status === "complete",
    ).length,
    avgScore: assessments.length
      ? Math.round(
          assessments.reduce(
            (sum, assessment) => sum + (assessment.computed?.grandTotal || 0),
            0,
          ) / assessments.length,
        )
      : 0,
    levelA: assessments.filter(
      (assessment) => assessment.computed?.rating === "Level A",
    ).length,
  };

  return (
    <div>
      <div className="section-heading">
        <div>
          <h1 className="section-title">Assessment History</h1>
          <p className="section-sub">
            {stats.total} total assessments · {stats.complete} complete ·
            {isAdmin ? `Average score: ${stats.avgScore}/1,000` : ""}
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => navigate("/assessment/new")}
        >
          + New Assessment
        </button>
      </div>

      {isAdmin && assessments.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "1rem",
            marginBottom: "1.5rem",
          }}
        >
          {[
            {
              label: "Total Assessments",
              value: stats.total,
              color: "var(--navy)",
              icon: "📋",
            },
            {
              label: "Completed",
              value: stats.complete,
              color: "var(--green)",
              icon: "✅",
            },
            {
              label: "Average Score",
              value: `${stats.avgScore}`,
              color: "var(--navy-mid)",
              icon: "📊",
            },
            {
              label: "Level A Institutions",
              value: stats.levelA,
              color: "var(--gold)",
              icon: "🏆",
            },
          ].map((stat) => (
            <div
              key={stat.label}
              className="card"
              style={{ padding: "1rem 1.25rem" }}
            >
              <div style={{ fontSize: "1.5rem", marginBottom: "0.25rem" }}>
                {stat.icon}
              </div>
              <div
                style={{
                  fontSize: "1.6rem",
                  fontWeight: 800,
                  color: stat.color,
                  fontFamily: "var(--font-display)",
                }}
              >
                {stat.value}
              </div>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "var(--text-3)",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: "0.75rem",
          marginBottom: "1.25rem",
          alignItems: "center",
        }}
      >
        <input
          className="form-input"
          style={{ maxWidth: "300px" }}
          placeholder="🔍 Search institution or assessor…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <div
          className="tabs"
          style={{ margin: 0, borderBottom: "none", gap: "0.25rem" }}
        >
          {[
            ["all", "All"],
            ["draft", "Drafts"],
            ["complete", "Complete"],
          ].map(([value, label]) => (
            <button
              key={value}
              className={`tab-btn${filter === value ? " active" : ""}`}
              onClick={() => setFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div
          style={{ display: "flex", justifyContent: "center", padding: "3rem" }}
        >
          <div
            className="spinner"
            style={{ width: 36, height: 36, borderWidth: 3 }}
          />
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            {assessments.length === 0 ? "📋" : "🔍"}
          </div>
          <div className="empty-state-title">
            {assessments.length === 0
              ? "No assessments yet"
              : "No results found"}
          </div>
          <div className="empty-state-sub">
            {assessments.length === 0
              ? "Start your first PHC capacity assessment using the button above."
              : "Try adjusting your search or filter."}
          </div>
          {assessments.length === 0 && (
            <button
              className="btn btn-primary"
              style={{ marginTop: "1rem" }}
              onClick={() => navigate("/assessment/new")}
            >
              + New Assessment
            </button>
          )}
        </div>
      ) : (
        <div className="history-list">
          {filtered.map((assessment) => (
            <HistoryItem
              key={assessment._id}
              assessment={assessment}
              onDelete={handleDelete}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      )}
    </div>
  );
}
